import React from 'react';
import PropTypes from 'prop-types';
import { Card, CardContent, Box, Typography, IconButton, Button, Collapse } from '@mui/material';
import { ZoomOutMap, ExpandMore, PushPin, PushPinOutlined } from '@mui/icons-material';
import { PieChartView, BarChartView } from './ChartComponents';
import { useTranslation } from 'react-i18next';
import DataLimitSelector from './DataLimitSelector';
import { reportStyles } from '../../shared/globalSX';
import { ICONS } from '../../shared/icons';
import { COLORS } from '../../shared/colors';
import { Skeleton } from '@mui/material';

const ChartCard = React.memo(({ 
  title, 
  data, 
  chartView, 
  setChartView, 
  onZoomClick, 
  height = 500,
  chartHeight = 390,
  barFill,
  outerRadius = 120,
  dataLimit,
  setDataLimit,
  chartType,
  defaultExpanded = true,
  isPinned = false,
  onPin,
  isLoading = false
}) => {
  const { t } = useTranslation();
  // If isPinned is true, we force the chart to be expanded
  const [isExpanded, setIsExpanded] = React.useState(isPinned || defaultExpanded);

  // Sync isExpanded with isPinned when it changes
  React.useEffect(() => {
    if (isPinned) {
      setIsExpanded(true);
    }
  }, [isPinned]);

  return (
    <Card sx={{ height: 'auto', transition: 'height 0.3s' }} data-chart-type={chartType}>
      <CardContent sx={{ pb: isExpanded ? 0 : '16px !important', '&:last-child': { pb: isExpanded ? 0 : '16px !important' } }}>
        <Box 
          display="flex" 
          justifyContent="space-between" 
          alignItems="center" 
          pb={1.5}
          mb={isExpanded ? 3 : 0} 
          flexWrap="wrap" 
          gap={1.5}
          sx={{ borderBottom: `1px solid ${COLORS.brand.primaryDark}` }}
        >
          <Box display="flex" alignItems="center" gap={1} sx={{ cursor: 'pointer', flex: '1 1 auto', minWidth: 0 }} onClick={() => setIsExpanded(!isExpanded)}>
            <IconButton size="small" sx={{ color: 'text.secondary', p: 0 }}>
              {isExpanded ? <ExpandMore /> : <ExpandMore sx={{ transform: 'rotate(-90deg)' }} />}
            </IconButton>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 500,
                fontSize: { xs: '0.95rem', sm: '1rem', md: '1.25rem' },
                lineHeight: 1.3,
                minWidth: 0,
                color: COLORS.brand.primaryDark 
              }}
            >
              {title}
            </Typography>
          </Box>
          <Box display="flex" gap={1} flexWrap="nowrap" sx={{ flexShrink: 0, alignItems: 'center' }}>
            {dataLimit !== undefined && setDataLimit && (
              <DataLimitSelector value={dataLimit} onChange={(e) => setDataLimit(e.target.value)} />
            )}
            <Button
              variant="outlined"
              size="small"
              startIcon={<Box component="img" src={ICONS.pieChart} sx={{ width: 18, height: 18 }} />}
              onClick={() => setChartView('pie')}
              sx={{ 
                ...reportStyles.buttonOutlined,
                minWidth: { xs: '36px', sm: 'auto' },
                px: { xs: 1, sm: 1.5 },
                '& .MuiButton-startIcon': { 
                  margin: { xs: 0, sm: '0 8px 0 -4px' } 
                },
                '& .MuiButton-startIcon + *': {
                  display: { xs: 'none', sm: 'inline' }
                }
              }}
            >
              {t("reports.charts.pie")}
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Box component="img" src={ICONS.barGraph} sx={{ width: 18, height: 18 }} />}
              onClick={() => setChartView('bar')}
              sx={{ 
                ...reportStyles.buttonOutlined,
                minWidth: { xs: '36px', sm: 'auto' },
                px: { xs: 1, sm: 1.5 },
                '& .MuiButton-startIcon': { 
                  margin: { xs: 0, sm: '0 8px 0 -4px' } 
                },
                '& .MuiButton-startIcon + *': {
                  display: { xs: 'none', sm: 'inline' }
                }
              }}
            >
              {t("reports.charts.bar")}
            </Button>
          </Box>
        </Box>

        <Collapse in={isExpanded}>
          <Box sx={{ mt: 3, height: chartHeight, position: 'relative' }}>
            {(isLoading || data === null) ? (
              <Box sx={{ height: chartHeight, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
                {chartView === 'pie' ? (
                  <Skeleton variant="circular" width={outerRadius * 2} height={outerRadius * 2} animation="wave" />
                ) : (
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end', height: '100%', width: '100%', px: 2 }}>
                    {[...Array(5)].map((_, i) => (
                      <Skeleton key={i} variant="rounded" width="100%" height={`${20 + Math.random() * 60}%`} animation="wave" sx={{ borderRadius: '6px 6px 0 0' }} />
                    ))}
                  </Box>
                )}
                <Skeleton variant="text" width="60%" sx={{ mt: 2 }} />
              </Box>
            ) : (!data || data.length === 0) ? (
              <Box sx={{ height: chartHeight, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="body2" color="text.secondary">{t("reports.noDataAvailable")}</Typography>
              </Box>
            ) : (
              <>
                {chartView === 'pie' && <PieChartView data={data} height={chartHeight - 40} outerRadius={outerRadius} />}
                {chartView === 'bar' && <BarChartView data={data} height={chartHeight - 40} barFill={barFill} />}
                
                {/* Fixed Bottom Right Actions with Premium Shadow and Click Effect */}
                <Box sx={{ 
                  position: 'absolute', 
                  bottom: 12, 
                  right: 12, 
                  display: 'flex', 
                  gap: 1.5,
                  zIndex: 2
                }}>
                  <IconButton 
                    size="small" 
                    onClick={onPin} 
                    sx={{ 
                      ...reportStyles.floatingAction,
                      color: isPinned ? COLORS.brand.primaryDark : 'text.secondary'
                    }}
                  >
                    {isPinned ? <PushPin fontSize="small" /> : <PushPinOutlined fontSize="small" />}
                  </IconButton>
                  {onZoomClick && (
                    <IconButton 
                      size="small" 
                      onClick={onZoomClick} 
                      sx={reportStyles.floatingAction}
                    >
                      <ZoomOutMap fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              </>
            )}
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
}, (prevProps, nextProps) => 
  prevProps.chartView === nextProps.chartView &&
  prevProps.isLoading === nextProps.isLoading &&
  prevProps.data?.length === nextProps.data?.length &&
  prevProps.dataLimit === nextProps.dataLimit &&
  prevProps.defaultExpanded === nextProps.defaultExpanded &&
  prevProps.isPinned === nextProps.isPinned &&
  JSON.stringify(prevProps.data) === JSON.stringify(nextProps.data)
);

ChartCard.propTypes = {
  title: PropTypes.string.isRequired,
  data: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string.isRequired,
    tickets: PropTypes.number.isRequired,
    color: PropTypes.string
  })),
  chartView: PropTypes.oneOf(['pie', 'bar']).isRequired,
  setChartView: PropTypes.func.isRequired,
  onZoomClick: PropTypes.func,
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  chartHeight: PropTypes.number,
  barFill: PropTypes.string,
  outerRadius: PropTypes.number,
  dataLimit: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  setDataLimit: PropTypes.func,
  chartType: PropTypes.string,
  defaultExpanded: PropTypes.bool,
  isPinned: PropTypes.bool,
  onPin: PropTypes.func,
  isLoading: PropTypes.bool
};

export default ChartCard;
